import {useCallback} from 'react';
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import styled from 'styled-components';
import {DayPopulation, FloatingPopulation} from '@/map/@types';
import {Flexbox} from '@/styled-components';
import {palette} from '@/styles';
import {ChartTitle} from '../../StyledComponents';
import {CHART_COLORS, POPULATION_TIME_HOUR} from '../constants';
import {chartWithComma} from '../utils';
import {CustomizedTooltip} from './CustomizedChartItem';

export interface IProps {
  type: string;
  data: FloatingPopulation & {
    populationTotal?: DayPopulation[];
  };
  compareItemName: string;
  compareItemIndex: number;
  tabIndex: number;
}

export const TimeTrafficSynchronizedAreaChart = ({
  type,
  data: rawdata,
  compareItemName,
  compareItemIndex,
  tabIndex,
}: IProps) => {
  const getChartData = useCallback(() => {
    const {day, week, populationTotal} = rawdata;

    if (
      !populationTotal ||
      populationTotal.length === 0 ||
      !day ||
      day.length === 0 ||
      !week ||
      week.length === 0
    ) {
      return null;
    }

    const dataArr = [populationTotal, day, week];
    const tabData = dataArr[tabIndex];

    const chartData: Array<{name: string; value: number}> = tabData.map(
      (item, index) => {
        return {
          name: POPULATION_TIME_HOUR[index].name,
          value: Math.round(item.totalPopulation || 0),
        };
      }
    );

    return chartData;
  }, [rawdata, tabIndex]);

  const chartData = getChartData();

  if (!chartData) {
    return null;
  }

  return (
    <Root>
      <Flexbox>
        <ChartTitle>{compareItemName}</ChartTitle>
      </Flexbox>
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} syncId={type}>
            <CartesianGrid
              vertical={false}
              stroke={palette.gray100}
              strokeWidth={1}
            />
            <XAxis
              dataKey="name"
              interval={0}
              tick={{fontSize: 12, fill: palette.gray600}}
              tickLine={false}
            />
            <YAxis
              interval={0}
              tick={{fontSize: 12, fill: palette.gray400}}
              axisLine={false}
              tickLine={false}
              tickFormatter={chartWithComma}
            />
            <Tooltip
              content={<CustomizedTooltip customName={compareItemName} />}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={CHART_COLORS[compareItemIndex]}
              fill={CHART_COLORS[compareItemIndex]}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChartWrapper = styled.div`
  height: 98px;
`;
